import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Container,
    Typography,
    InputBase,
    Button,
    Dialog,
    DialogContent,
    IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import StarsIcon from "@mui/icons-material/Stars";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    fetchAllServices,
    deriveCategories,
    filterMemberVisible,
    formatPrice,
    TWO_SENSE_SHOP_ID,
} from "../api/services";
import { useJsonLd, serviceCatalogSchema } from "../utils/seo";
import Reveal from "../components/Reveal";
import VideoBackground from "../components/VideoBackground";
import {
    heroContainerSx,
    useHeroPoster,
    HERO_VIDEO_SRC,
    HERO_SCRIM,
} from "../utils/heroBackground";

const ALL_KEY = "__all__";

const useInView = (threshold = 0.12) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => entry.isIntersecting && setInView(true),
            { threshold },
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [threshold]);
    return [ref, inView];
};

/* Skeleton card — mirrors the real card's editorial layout (tinted image
   tile with hairline frame + content area below) so the grid doesn't
   re-flow when real cards mount. */
const SkeletonCard = () => (
    <Box
        sx={{
            width: "100%",
            background: "rgba(255, 255, 255, 0.92)",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
            display: "flex",
            flexDirection: "column",
        }}
    >
        {/* Image tile — same tinted-cream gradient as the real fallback */}
        <Box
            sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "4 / 3",
                overflow: "hidden",
                borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                background:
                    "linear-gradient(135deg, #f7f5f2 0%, #ece8e2 50%, #d9d4cc 100%)",
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: { xs: 12, md: 16 },
                    border: "1px solid rgba(0, 0, 0, 0.14)",
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "12%",
                    right: "12%",
                    height: "1px",
                    background: "rgba(0, 0, 0, 0.1)",
                    transform: "rotate(-8deg)",
                    transformOrigin: "center",
                    pointerEvents: "none",
                }}
            />
            <Box
                className="sp-skel"
                sx={{
                    position: "absolute",
                    top: { xs: 22, md: 28 },
                    left: { xs: 22, md: 28 },
                    height: 8,
                    width: 56,
                }}
            />
            <Box
                className="sp-skel"
                sx={{
                    position: "absolute",
                    bottom: { xs: 22, md: 28 },
                    right: { xs: 22, md: 28 },
                    height: 8,
                    width: 28,
                }}
            />
            <Box
                className="sp-skel"
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    height: 36,
                    width: 78,
                    borderRadius: 1,
                }}
            />
        </Box>

        {/* Content area */}
        <Box
            sx={{
                p: { xs: "1.5rem 1.5rem 1.6rem", md: "1.85rem" },
                display: "flex",
                flexDirection: "column",
                flex: 1,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    mb: 2,
                }}
            >
                <Box className="sp-skel" sx={{ height: 9, width: 76 }} />
                <Box className="sp-skel" sx={{ height: 9, width: 32 }} />
            </Box>
            <Box className="sp-skel" sx={{ height: 18, width: "82%", mb: 1 }} />
            <Box className="sp-skel" sx={{ height: 18, width: "58%", mb: 2 }} />
            <Box className="sp-skel" sx={{ height: 1, width: 32, mb: 2 }} />
            <Box className="sp-skel" sx={{ height: 9, width: "92%", mb: 0.75 }} />
            <Box className="sp-skel" sx={{ height: 9, width: "68%", mb: 2.5 }} />
            <Box
                sx={{
                    mt: "auto",
                    pt: 2,
                    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                }}
            >
                <Box>
                    <Box className="sp-skel" sx={{ height: 8, width: 26, mb: 1 }} />
                    <Box className="sp-skel" sx={{ height: 26, width: 70, mb: 1 }} />
                    <Box className="sp-skel" sx={{ height: 8, width: 52 }} />
                </Box>
                <Box className="sp-skel" sx={{ height: 9, width: 60 }} />
            </Box>
        </Box>
    </Box>
);

/* Lazy mount — renders a placeholder of identical height until the wrapper
   scrolls within ~600px of the viewport, then swaps in the real children.
   Saves the cost of mounting all 199 cards up-front. */
const LazyCard = ({ children, minHeight }) => {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);
    useEffect(() => {
        if (shown) return;
        const node = ref.current;
        if (!node) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShown(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "600px 0px" },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [shown]);
    return (
        <Box
            ref={ref}
            sx={{
                height: "100%",
                minHeight: shown ? "auto" : minHeight,
                display: "flex",
            }}
        >
            {shown ? children : <SkeletonCard />}
        </Box>
    );
};

/* Trim a value and drop the literal "null"/"undefined" strings the API
   sometimes returns, so they never render as text. */
const cleanStr = (v) => {
    if (!v) return "";
    const s = String(v).trim();
    if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") return "";
    return s;
};

/* Card image — shows the service's own photo, sourced from the labeau API.
   `src` is the API's image_url (a presigned S3 link the backend re-signs on
   every read, so a fresh page load always gets a working URL). When it is
   absent, or fails to load — a service with no image, or a link that expired
   on a session left open past its ~1h window — the card shows the shop logo on
   a branded tile. */
const CardImage = ({ src, alt }) => {
    const [failed, setFailed] = useState(false);

    if (!src || failed) {
        return (
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                        "linear-gradient(135deg, #f7f5f2 0%, #efe9e1 55%, #e6ded3 100%)",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: { xs: 12, md: 16 },
                        border: "1px solid rgba(0, 0, 0, 0.12)",
                        pointerEvents: "none",
                    }}
                />
                <Box
                    component="img"
                    src="/images/logo.png"
                    alt={alt}
                    loading="lazy"
                    sx={{
                        width: "52%",
                        maxWidth: 150,
                        objectFit: "contain",
                        opacity: 0.85,
                        transition: "transform 0.7s cubic-bezier(0.23,1,0.32,1)",
                        ".sp-card:hover &": { transform: "scale(1.04)" },
                    }}
                />
            </Box>
        );
    }

    return (
        <Box
            component="img"
            src={src}
            alt={alt}
            loading="lazy"
            onError={() => setFailed(true)}
            sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                filter: "saturate(0.95) contrast(1.03)",
                transition: "transform 0.7s cubic-bezier(0.23,1,0.32,1)",
                ".sp-card:hover &": { transform: "scale(1.04)" },
            }}
        />
    );
};

const ServicesPage = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeCategory, setActiveCategory] = useState(ALL_KEY);
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);

    const [headerRef, headerInView] = useInView();
    const heroPoster = useHeroPoster();

    /* ── Fetch services on mount ── */
    useEffect(() => {
        const controller = new AbortController();
        let alive = true;

        setLoading(true);
        setError(null);

        fetchAllServices({ shopId: TWO_SENSE_SHOP_ID, signal: controller.signal })
            .then((data) => {
                if (!alive) return;
                // Only surface services the shop has marked visible to members.
                setServices(filterMemberVisible(data));
                setLoading(false);
            })
            .catch((err) => {
                if (!alive || err.name === "AbortError") return;
                setError(err);
                setLoading(false);
            });

        return () => {
            alive = false;
            controller.abort();
        };
    }, []);

    const categories = useMemo(() => deriveCategories(services), [services]);

    /* Publish the live menu as an OfferCatalog once it has loaded, so search
       engines can surface individual services and their prices rather than
       just the page. Recomputed only when the fetched list changes. */
    const catalogSchema = useMemo(
        () => serviceCatalogSchema(services, categories),
        [services, categories],
    );
    useJsonLd("ld-service-catalog", catalogSchema);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return services.filter((s) => {
            if (activeCategory !== ALL_KEY && s?.category?.id !== activeCategory) return false;
            if (!q) return true;
            const haystack = `${s.name || ""} ${s.description || ""} ${s.special_remark || ""}`.toLowerCase();
            return haystack.includes(q);
        });
    }, [services, activeCategory, search]);

    /* ── Translate currency labels to current locale ── */
    const currency = t("servicesPage.currency");
    const lang = (i18n.language || "en").split("-")[0];

    /* Bilingual service names like "B01 卸甲（…）REMOVAL OF NATURAL NAILS ONLY"
       — split into a quiet code + the human title for cleaner card hierarchy.
       Whitespace between the code and the title is optional so codes attached
       directly to Chinese characters (e.g. "B44上臂") still get extracted. */
    const splitName = (name) => {
        if (!name) return { code: "", title: "" };
        const match = name.match(/^([A-Z]\d+)\s*(.*)$/);
        if (match) return { code: match[1], title: match[2].trim() };
        return { code: "", title: name.trim() };
    };

    return (
        <Box
            sx={{
                background: "#f6f3ee",
                minHeight: "100vh",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <style>{`
                @keyframes sp-rise {
                    from { opacity: 0; transform: translateY(28px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes sp-fade {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                @keyframes sp-line-grow {
                    from { transform: scaleX(0); }
                    to   { transform: scaleX(1); }
                }
                @keyframes sp-shimmer {
                    0%   { background-position: -480px 0; }
                    100% { background-position: 480px 0; }
                }
                .sp-skel {
                    position: relative;
                    overflow: hidden;
                    background: rgba(0, 0, 0, 0.05);
                }
                .sp-skel::after {
                    content: "";
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(
                        90deg,
                        rgba(0,0,0,0) 0%,
                        rgba(255,255,255,0.55) 50%,
                        rgba(0,0,0,0) 100%
                    );
                    background-size: 480px 100%;
                    background-repeat: no-repeat;
                    background-position: -480px 0;
                    animation: sp-shimmer 1.6s linear infinite;
                }
                .sp-card {
                    transition: transform 0.55s cubic-bezier(0.23,1,0.32,1),
                                box-shadow 0.55s cubic-bezier(0.23,1,0.32,1),
                                border-color 0.55s ease,
                                background 0.55s ease;
                }
                .sp-card:hover {
                    transform: translateY(-6px);
                    border-color: rgba(0, 0, 0, 0.65);
                    box-shadow: inset 0 1px 0 rgba(255,255,255,0.85),
                                0 32px 80px rgba(0, 0, 0, 0.12);
                    background: rgba(255, 255, 255, 0.95);
                }
                .sp-card:hover .sp-card-line {
                    width: 100% !important;
                }
                .sp-card:hover .sp-reserve {
                    color: #000000 !important;
                }
                .sp-card:hover .sp-reserve-arrow {
                    transform: translate(3px, -3px);
                }
                /* Refined category rail */
                .sp-cat-rail {
                    overflow-x: auto;
                    overflow-y: hidden;
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .sp-cat-rail::-webkit-scrollbar { display: none; }
                .sp-cat {
                    position: relative;
                    cursor: pointer;
                    flex-shrink: 0;
                    padding: 0.6rem 0;
                    color: rgba(0, 0, 0, 0.7);
                    transition: color 0.4s ease;
                    background: none;
                    border: none;
                    font-family: inherit;
                }
                .sp-cat:hover { color: #000000; }
                .sp-cat-active {
                    color: #000000 !important;
                }
                .sp-cat-active::after {
                    content: "";
                    position: absolute;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    height: 1px;
                    background: #000000;
                    transform: scaleX(1);
                    transform-origin: left;
                    transition: transform 0.45s cubic-bezier(0.23,1,0.32,1);
                }
                .sp-cat::after {
                    content: "";
                    position: absolute;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    height: 1px;
                    background: #000000;
                    transform: scaleX(0);
                    transform-origin: right;
                    transition: transform 0.45s cubic-bezier(0.23,1,0.32,1);
                }
                .sp-cat:hover::after {
                    transform: scaleX(1);
                    transform-origin: left;
                }
                /* Search bar focus state */
                .sp-search-shell {
                    transition: border-color 0.4s ease, box-shadow 0.4s ease;
                }
                .sp-search-shell:focus-within {
                    border-color: #000000;
                    box-shadow: 0 1px 0 #000000;
                }
            `}</style>

            {/* ═══════════════════════════════════════════
                Header band — dark hero strip
            ═══════════════════════════════════════════ */}
            <Box
                ref={headerRef}
                sx={{
                    ...heroContainerSx,
                    pt: { xs: "8.5rem", md: "11rem" },
                    pb: { xs: "4rem", md: "6rem" },
                    color: "#ffffff",
                }}
            >
                <VideoBackground
                    src={HERO_VIDEO_SRC}
                    poster={heroPoster}
                    overlay={HERO_SCRIM}
                    sx={{ zIndex: 0 }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            "radial-gradient(ellipse at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 55%)",
                        pointerEvents: "none",
                        zIndex: 1,
                    }}
                />

                <Container
                    maxWidth="lg"
                    sx={{ position: "relative", zIndex: 2, textAlign: "center", px: { xs: 3, md: 6 } }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                            mb: { xs: 3, md: 4 },
                            opacity: headerInView ? 1 : 0,
                            animation: headerInView ? "sp-fade 0.9s ease 0.1s both" : "none",
                        }}
                    >
                        <Box
                            sx={{
                                width: { xs: "30px", md: "44px" },
                                height: "1px",
                                background: "#ffffff",
                                transformOrigin: "right",
                                animation: headerInView ? "sp-line-grow 0.9s ease 0.2s both" : "none",
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: { xs: "0.65rem", md: "0.7rem" },
                                fontWeight: 500,
                                letterSpacing: "4px",
                                textTransform: "uppercase",
                                color: "#ffffff",
                            }}
                        >
                            {t("servicesPage.badge")}
                        </Typography>
                        <Box
                            sx={{
                                width: { xs: "30px", md: "44px" },
                                height: "1px",
                                background: "#ffffff",
                                transformOrigin: "left",
                                animation: headerInView ? "sp-line-grow 0.9s ease 0.2s both" : "none",
                            }}
                        />
                    </Box>

                    <Typography
                        variant="h1"
                        sx={{
                            fontFamily: '"Cormorant Garamond", Georgia, serif',
                            fontWeight: 400,
                            fontSize: { xs: "2.4rem", sm: "3.2rem", md: "4.4rem" },
                            color: "#ffffff",
                            lineHeight: 1.1,
                            letterSpacing: "-0.5px",
                            mb: { xs: 2, md: 3 },
                            opacity: headerInView ? 1 : 0,
                            animation: headerInView ? "sp-rise 1s ease 0.25s both" : "none",
                        }}
                    >
                        {t("servicesPage.title")}
                    </Typography>

                    <Typography
                        sx={{
                            fontFamily: '"Cormorant Garamond", serif',
                            fontStyle: "italic",
                            fontSize: { xs: "1rem", md: "1.2rem" },
                            color: "rgba(255, 255, 255, 0.7)",
                            fontWeight: 300,
                            maxWidth: "620px",
                            mx: "auto",
                            opacity: headerInView ? 1 : 0,
                            animation: headerInView ? "sp-rise 1s ease 0.4s both" : "none",
                        }}
                    >
                        — {t("servicesPage.description")}
                    </Typography>

                    {!loading && !error && services.length > 0 && (
                        <Typography
                            sx={{
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                letterSpacing: "3px",
                                textTransform: "uppercase",
                                color: "rgba(255, 255, 255, 0.45)",
                                mt: { xs: 3, md: 4 },
                                opacity: headerInView ? 1 : 0,
                                animation: headerInView ? "sp-fade 1s ease 0.6s both" : "none",
                            }}
                        >
                            {`${services.length} ${
                                services.length === 1
                                    ? t("servicesPage.service")
                                    : t("servicesPage.services")
                            } · ${categories.length} ${
                                lang === "cn" ? "项分类" : lang === "bm" ? "kategori" : "categories"
                            }`}
                        </Typography>
                    )}
                </Container>
            </Box>

            {/* ═══════════════════════════════════════════
                Filters & search
            ═══════════════════════════════════════════ */}
            <Container maxWidth="lg" sx={{ position: "relative", px: { xs: 3, md: 6 }, py: { xs: 4, md: 7 } }}>
                {/* ── Editorial search bar — borderless, hairline-only ── */}
                <Reveal y={18} duration={0.7}>
                <Box
                    className="sp-search-shell"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 1.5, md: 2.5 },
                        borderBottom: "1px solid rgba(0, 0, 0, 0.25)",
                        pb: { xs: 1.5, md: 2 },
                        mb: { xs: 4, md: 5 },
                    }}
                >
                    <SearchIcon sx={{ fontSize: { xs: "1.15rem", md: "1.3rem" }, color: "#000000" }} />
                    <InputBase
                        placeholder={t("servicesPage.search")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{
                            flex: 1,
                            fontFamily: '"Cormorant Garamond", serif',
                            fontSize: { xs: "1.1rem", md: "1.4rem" },
                            color: "#000000",
                            "& input": {
                                p: 0,
                                "&::placeholder": {
                                    fontFamily: '"Cormorant Garamond", serif',
                                    fontStyle: "italic",
                                    color: "rgba(0, 0, 0, 0.4)",
                                    opacity: 1,
                                    letterSpacing: "0.3px",
                                },
                            },
                        }}
                    />
                    {search && (
                        <IconButton
                            size="small"
                            onClick={() => setSearch("")}
                            sx={{
                                color: "rgba(0, 0, 0, 0.5)",
                                "&:hover": { color: "#000000", background: "transparent" },
                            }}
                        >
                            <CloseIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    )}
                    <Typography
                        sx={{
                            fontFamily: '"Cormorant Garamond", serif',
                            fontStyle: "italic",
                            color: "#666666",
                            fontSize: { xs: "0.85rem", md: "0.95rem" },
                            whiteSpace: "nowrap",
                            display: { xs: "none", sm: "block" },
                            pl: 1.5,
                            borderLeft: "1px solid rgba(0, 0, 0, 0.18)",
                        }}
                    >
                        {t("servicesPage.showing")} {filtered.length} {t("servicesPage.of")} {services.length}
                    </Typography>
                </Box>
                </Reveal>

                {/* ── Refined category rail — text-link style ── */}
                {categories.length > 0 && (
                    <Reveal y={18} duration={0.7} delay={0.08}>
                    <Box sx={{ mb: { xs: 4, md: 6 } }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 1.5,
                                mb: { xs: 2, md: 2.5 },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: "0.6rem", md: "0.65rem" },
                                    fontWeight: 500,
                                    letterSpacing: "3.5px",
                                    textTransform: "uppercase",
                                    color: "#000000",
                                }}
                            >
                                {t("servicesPage.filterAll")} —
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: '"Cormorant Garamond", serif',
                                    fontStyle: "italic",
                                    fontSize: { xs: "0.85rem", md: "0.95rem" },
                                    color: "rgba(0, 0, 0, 0.45)",
                                }}
                            >
                                {categories.length}{" "}
                                {lang === "cn" ? "项分类" : lang === "bm" ? "kategori" : "categories"}
                            </Typography>
                        </Box>

                        <Box
                            className="sp-cat-rail"
                            sx={{
                                display: "flex",
                                gap: { xs: 2.5, md: 3.5 },
                                pb: 0,
                                borderBottom: "1px solid rgba(0, 0, 0, 0.18)",
                            }}
                        >
                            <Box
                                component="button"
                                onClick={() => setActiveCategory(ALL_KEY)}
                                className={`sp-cat ${activeCategory === ALL_KEY ? "sp-cat-active" : ""}`}
                                sx={{
                                    display: "flex",
                                    alignItems: "baseline",
                                    gap: 0.75,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <Typography
                                    component="span"
                                    sx={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontSize: "0.72rem",
                                        fontWeight: 500,
                                        letterSpacing: "2.5px",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {t("servicesPage.filterAll")}
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontFamily: '"Cormorant Garamond", serif',
                                        fontStyle: "italic",
                                        fontSize: "0.85rem",
                                        opacity: 0.85,
                                    }}
                                >
                                    {services.length}
                                </Typography>
                            </Box>

                            {categories.map((cat) => (
                                <Box
                                    key={cat.id}
                                    component="button"
                                    onClick={() => setActiveCategory(cat.id)}
                                    className={`sp-cat ${activeCategory === cat.id ? "sp-cat-active" : ""}`}
                                    sx={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        gap: 0.75,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontFamily: '"Inter", sans-serif',
                                            fontSize: "0.72rem",
                                            fontWeight: 500,
                                            letterSpacing: "2px",
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {cat.name}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontFamily: '"Cormorant Garamond", serif',
                                            fontStyle: "italic",
                                            fontSize: "0.85rem",
                                            opacity: 0.85,
                                        }}
                                    >
                                        {cat.count}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    </Reveal>
                )}

                {/* States: loading / error / empty / grid */}
                {loading && (
                    <Box>
                        <Typography
                            sx={{
                                fontFamily: '"Cormorant Garamond", serif',
                                fontStyle: "italic",
                                fontSize: { xs: "0.95rem", md: "1.05rem" },
                                color: "#666666",
                                mb: { xs: 3, md: 4 },
                                textAlign: "center",
                            }}
                        >
                            — {t("servicesPage.loading")}
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(2, 1fr)",
                                    md: "repeat(3, 1fr)",
                                },
                                gap: { xs: 2, md: 2.5 },
                            }}
                        >
                            {Array.from({ length: 9 }).map((_, i) => (
                                <SkeletonCard key={i} />
                            ))}
                        </Box>
                    </Box>
                )}

                {error && !loading && (
                    <Box sx={{ textAlign: "center", py: { xs: 8, md: 12 } }}>
                        <Typography
                            sx={{
                                fontFamily: '"Cormorant Garamond", serif',
                                fontSize: { xs: "1.4rem", md: "1.8rem" },
                                color: "#000000",
                                mb: 1.5,
                            }}
                        >
                            {t("servicesPage.errorTitle")}
                        </Typography>
                        <Typography sx={{ color: "#666666", fontSize: "0.95rem", fontWeight: 300 }}>
                            {t("servicesPage.errorBody")}
                        </Typography>
                    </Box>
                )}

                {/* Empty state — must NOT be gated on `services.length > 0`.
                    When the fetch succeeds but yields nothing, that guard left
                    the page rendering an entirely blank slab with no
                    explanation, which is how the member_visible filter bug
                    stayed invisible. */}
                {!loading && !error && filtered.length === 0 && (
                    <Box sx={{ textAlign: "center", py: { xs: 8, md: 12 } }}>
                        <Typography
                            sx={{
                                fontFamily: '"Cormorant Garamond", serif',
                                fontStyle: "italic",
                                fontSize: { xs: "1.2rem", md: "1.5rem" },
                                color: "#666666",
                            }}
                        >
                            — {t("servicesPage.empty")}
                        </Typography>
                    </Box>
                )}

                {!loading && !error && filtered.length > 0 && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "repeat(2, 1fr)",
                                md: "repeat(3, 1fr)",
                            },
                            gap: { xs: 2, md: 2.5 },
                        }}
                    >
                        {filtered.map((svc, idx) => {
                            const { code, title } = splitName(svc.name);
                            const price = svc.shop_price ?? svc.price;
                            const duration = svc.shop_duration_minutes || svc.duration_minutes || 0;
                            const reward = Number(svc.reward_point) || 0;
                            const cleanText = (v) => {
                                if (!v) return "";
                                const s = String(v).trim();
                                if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined")
                                    return "";
                                return s;
                            };
                            const desc = cleanText(svc.description) || cleanText(svc.special_remark);
                            // The service photo comes from the labeau API; when
                            // it is absent or fails to load CardImage shows the
                            // shop logo.
                            const rawImage = cleanText(svc.image_url);
                            return (
                                <LazyCard key={svc.id} minHeight={520}>
                                <Box
                                    className="sp-card"
                                    onClick={() => setSelected(svc)}
                                    sx={{
                                        position: "relative",
                                        overflow: "hidden",
                                        width: "100%",
                                        background: "rgba(255, 255, 255, 0.92)",
                                        border: "1px solid rgba(0, 0, 0, 0.14)",
                                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
                                        cursor: "pointer",
                                        display: "flex",
                                        flexDirection: "column",
                                        animation: `sp-rise 0.7s cubic-bezier(0.23,1,0.32,1) ${
                                            Math.min(idx, 18) * 0.04
                                        }s both`,
                                    }}
                                >
                                    {/* ── Image tile ── */}
                                    <Box
                                        sx={{
                                            position: "relative",
                                            width: "100%",
                                            aspectRatio: "4 / 3",
                                            overflow: "hidden",
                                            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                                            background: "#0a0a0a",
                                        }}
                                    >
                                        <CardImage src={rawImage} alt={title} />
                                    </Box>

                                    {/* ── Content area ── */}
                                    <Box
                                        sx={{
                                            position: "relative",
                                            p: { xs: "1.5rem 1.5rem 1.6rem", md: "1.85rem 1.85rem 1.85rem" },
                                            display: "flex",
                                            flexDirection: "column",
                                            flex: 1,
                                        }}
                                    >

                                    {/* category eyebrow + reference code */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "baseline",
                                            mb: 1.5,
                                            gap: 1,
                                            position: "relative",
                                            zIndex: 1,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "0.6rem",
                                                fontWeight: 500,
                                                letterSpacing: "3px",
                                                textTransform: "uppercase",
                                                color: "#000000",
                                                lineHeight: 1.4,
                                                flex: 1,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {svc.category?.name || "—"}
                                        </Typography>
                                        {code && (
                                            <Typography
                                                sx={{
                                                    fontFamily: '"Cormorant Garamond", serif',
                                                    fontStyle: "italic",
                                                    fontSize: "0.78rem",
                                                    fontWeight: 500,
                                                    color: "rgba(0, 0, 0, 0.55)",
                                                    flexShrink: 0,
                                                    letterSpacing: "0.5px",
                                                }}
                                            >
                                                — {code}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Title */}
                                    <Typography
                                        sx={{
                                            fontFamily: '"Cormorant Garamond", serif',
                                            fontSize: { xs: "1.2rem", md: "1.4rem" },
                                            fontWeight: 500,
                                            color: "#000000",
                                            lineHeight: 1.25,
                                            letterSpacing: "-0.2px",
                                            mb: 1.75,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            position: "relative",
                                            zIndex: 1,
                                        }}
                                    >
                                        {title}
                                    </Typography>

                                    {/* Hairline that grows on hover */}
                                    <Box
                                        className="sp-card-line"
                                        sx={{
                                            width: "32px",
                                            height: "1px",
                                            background: "#000000",
                                            mb: 2,
                                            transition: "width 0.6s cubic-bezier(0.23,1,0.32,1)",
                                            position: "relative",
                                            zIndex: 1,
                                        }}
                                    />

                                    {/* Description / remark snippet */}
                                    {desc && (
                                        <Typography
                                            sx={{
                                                fontSize: "0.85rem",
                                                color: "#666666",
                                                lineHeight: 1.7,
                                                fontWeight: 300,
                                                mb: 2,
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                position: "relative",
                                                zIndex: 1,
                                            }}
                                        >
                                            {desc}
                                        </Typography>
                                    )}

                                    {/* Bottom row: price + meta + cta */}
                                    <Box
                                        sx={{
                                            mt: "auto",
                                            pt: 2,
                                            borderTop: "1px solid rgba(0, 0, 0, 0.18)",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-end",
                                            gap: 1.5,
                                            position: "relative",
                                            zIndex: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontSize: "0.58rem",
                                                    fontWeight: 500,
                                                    letterSpacing: "3px",
                                                    textTransform: "uppercase",
                                                    color: "rgba(0, 0, 0, 0.45)",
                                                    mb: 0.25,
                                                }}
                                            >
                                                {currency}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: '"Cormorant Garamond", serif',
                                                    fontSize: { xs: "1.85rem", md: "2.15rem" },
                                                    fontWeight: 500,
                                                    color: "#000000",
                                                    lineHeight: 1,
                                                    letterSpacing: "-0.5px",
                                                    fontVariantNumeric: "tabular-nums",
                                                }}
                                            >
                                                {formatPrice(price)}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: { xs: 1.25, md: 1.75 },
                                                    mt: 1.25,
                                                }}
                                            >
                                                {duration > 0 && (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                                        <AccessTimeIcon sx={{ fontSize: "0.78rem", color: "#000000" }} />
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.7rem",
                                                                color: "#666666",
                                                                fontWeight: 400,
                                                                letterSpacing: "0.3px",
                                                            }}
                                                        >
                                                            {duration} {t("servicesPage.minutes")}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {reward > 0 && (
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                                        <StarsIcon sx={{ fontSize: "0.78rem", color: "#000000" }} />
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.7rem",
                                                                color: "#666666",
                                                                fontWeight: 400,
                                                                letterSpacing: "0.3px",
                                                            }}
                                                        >
                                                            {Math.round(reward)} {t("servicesPage.rewardPoints")}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>

                                        <Box
                                            className="sp-reserve"
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontSize: "0.62rem",
                                                fontWeight: 500,
                                                letterSpacing: "3px",
                                                textTransform: "uppercase",
                                                color: "rgba(0, 0, 0, 0.55)",
                                                whiteSpace: "nowrap",
                                                transition: "color 0.35s ease",
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontSize: "inherit",
                                                    fontWeight: "inherit",
                                                    letterSpacing: "inherit",
                                                    textTransform: "inherit",
                                                }}
                                            >
                                                {t("servicesPage.reserve")}
                                            </Typography>
                                            <ArrowOutwardIcon
                                                className="sp-reserve-arrow"
                                                sx={{
                                                    fontSize: "0.85rem",
                                                    transition: "transform 0.4s cubic-bezier(0.23,1,0.32,1)",
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    </Box>
                                </Box>
                                </LazyCard>
                            );
                        })}
                    </Box>
                )}
            </Container>

            {/* ═══════════════════════════════════════════
                Detail modal
            ═══════════════════════════════════════════ */}
            <Dialog
                open={Boolean(selected)}
                onClose={() => setSelected(null)}
                maxWidth="xs"
                fullWidth
                /* Overlay removed — transparent backdrop, no dim/blur. */
                slotProps={{
                    backdrop: {
                        sx: { backgroundColor: "transparent", backdropFilter: "none", WebkitBackdropFilter: "none" },
                    },
                }}
                PaperProps={{
                    sx: {
                        borderRadius: { xs: "12px", md: "16px" },
                        overflow: "hidden",
                        background: "linear-gradient(180deg, #ffffff 0%, #fbf7f1 100%)",
                        border: "1px solid rgba(0,0,0,0.06)",
                        boxShadow:
                            "0 50px 130px rgba(0,0,0,0.34), 0 18px 48px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.8)",
                        m: { xs: 2, md: 3 },
                    },
                }}
            >
                {selected &&
                    (() => {
                        const { code, title } = splitName(selected.name);
                        const price = selected.shop_price ?? selected.price;
                        const duration = selected.shop_duration_minutes || selected.duration_minutes || 0;
                        const reward = Number(selected.reward_point) || 0;
                        const desc = cleanStr(selected.description);
                        const remark = cleanStr(selected.special_remark);
                        const heroSrc = cleanStr(selected.image_url);
                        const pillSx = {
                            display: "flex",
                            alignItems: "center",
                            gap: 0.6,
                            px: 1.4,
                            py: 0.65,
                            borderRadius: "999px",
                            border: "1px solid rgba(0,0,0,0.12)",
                            background: "rgba(255,255,255,0.7)",
                        };
                        return (
                            <Box>
                                {/* ── Hero image band ── */}
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        height: { xs: 184, md: 224 },
                                        overflow: "hidden",
                                        background: "#0a0a0a",
                                    }}
                                >
                                    <CardImage src={heroSrc} alt={title} />
                                    {/* legibility scrim */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            background:
                                                "linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0) 32%, rgba(0,0,0,0.62) 100%)",
                                            pointerEvents: "none",
                                        }}
                                    />
                                    {/* close */}
                                    <IconButton
                                        onClick={() => setSelected(null)}
                                        sx={{
                                            position: "absolute",
                                            top: 12,
                                            right: 12,
                                            width: 38,
                                            height: 38,
                                            color: "#fff",
                                            background: "rgba(0,0,0,0.35)",
                                            backdropFilter: "blur(8px)",
                                            WebkitBackdropFilter: "blur(8px)",
                                            border: "1px solid rgba(255,255,255,0.25)",
                                            transition: "all 0.3s ease",
                                            "&:hover": { background: "rgba(0,0,0,0.6)", transform: "rotate(90deg)" },
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                    {/* category + code overlaid bottom-left */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            left: { xs: 20, md: 28 },
                                            right: 60,
                                            bottom: { xs: 16, md: 20 },
                                            display: "flex",
                                            alignItems: "baseline",
                                            gap: 1.5,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "0.6rem",
                                                fontWeight: 600,
                                                letterSpacing: "3px",
                                                textTransform: "uppercase",
                                                color: "rgba(255,255,255,0.92)",
                                            }}
                                        >
                                            {selected.category?.name || "—"}
                                        </Typography>
                                        {code && (
                                            <Typography
                                                sx={{
                                                    fontFamily: '"Cormorant Garamond", serif',
                                                    fontStyle: "italic",
                                                    fontSize: "0.92rem",
                                                    color: "rgba(255,255,255,0.78)",
                                                }}
                                            >
                                                — {code}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>

                                <DialogContent sx={{ p: { xs: "1.6rem 1.5rem 1.9rem", md: "2rem 2.4rem 2.4rem" } }}>
                                    {/* Title */}
                                    <Typography
                                        sx={{
                                            fontFamily: '"Cormorant Garamond", serif',
                                            fontWeight: 500,
                                            fontSize: { xs: "1.5rem", md: "1.95rem" },
                                            color: "#000000",
                                            lineHeight: 1.22,
                                            letterSpacing: "-0.4px",
                                            mb: 1.5,
                                        }}
                                    >
                                        {title}
                                    </Typography>

                                    <Box sx={{ width: 36, height: "1px", background: "#000000", mb: 2.25 }} />

                                    {/* Price + meta */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-end",
                                            justifyContent: "space-between",
                                            flexWrap: "wrap",
                                            gap: 1.5,
                                            pb: 2.25,
                                            mb: desc || remark ? 2.25 : 2.75,
                                            borderBottom: "1px solid rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1.1 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: "0.58rem",
                                                    fontWeight: 600,
                                                    letterSpacing: "2.5px",
                                                    textTransform: "uppercase",
                                                    color: "rgba(0,0,0,0.4)",
                                                    pb: 0.8,
                                                }}
                                            >
                                                {currency}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: '"Cormorant Garamond", serif',
                                                    fontSize: { xs: "2.3rem", md: "2.85rem" },
                                                    fontWeight: 500,
                                                    color: "#000000",
                                                    lineHeight: 1,
                                                    letterSpacing: "-0.5px",
                                                    fontVariantNumeric: "tabular-nums",
                                                }}
                                            >
                                                {formatPrice(price)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                            {duration > 0 && (
                                                <Box sx={pillSx}>
                                                    <AccessTimeIcon sx={{ fontSize: "0.85rem", color: "#000" }} />
                                                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#333" }}>
                                                        {duration} {t("servicesPage.minutes")}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {reward > 0 && (
                                                <Box sx={pillSx}>
                                                    <StarsIcon sx={{ fontSize: "0.85rem", color: "#000" }} />
                                                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#333" }}>
                                                        {Math.round(reward)} {t("servicesPage.rewardPoints")}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    {desc && (
                                        <Box sx={{ mb: remark ? 2.25 : 2.75 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: "0.58rem",
                                                    fontWeight: 600,
                                                    letterSpacing: "3px",
                                                    textTransform: "uppercase",
                                                    color: "rgba(0,0,0,0.5)",
                                                    mb: 1,
                                                }}
                                            >
                                                {t("servicesPage.modal.description")}
                                            </Typography>
                                            <Typography sx={{ fontSize: "0.9rem", color: "#5a5a5a", lineHeight: 1.85, fontWeight: 300 }}>
                                                {desc}
                                            </Typography>
                                        </Box>
                                    )}

                                    {remark && (
                                        <Box sx={{ mb: 2.75, pl: 2, borderLeft: "2px solid rgba(0,0,0,0.45)" }}>
                                            <Typography
                                                sx={{
                                                    fontFamily: '"Cormorant Garamond", serif',
                                                    fontStyle: "italic",
                                                    fontSize: "0.95rem",
                                                    color: "#5a5a5a",
                                                    lineHeight: 1.75,
                                                }}
                                            >
                                                “{remark}”
                                            </Typography>
                                        </Box>
                                    )}

                                    <Button
                                        fullWidth
                                        endIcon={<ArrowOutwardIcon sx={{ fontSize: "1rem !important" }} />}
                                        onClick={() => {
                                            const fullName = selected ? selected.name : "";
                                            setSelected(null);
                                            navigate(`/appointment?service=${encodeURIComponent(fullName)}`);
                                        }}
                                        sx={{
                                            fontFamily: '"Inter", sans-serif',
                                            fontWeight: 500,
                                            fontSize: "0.72rem",
                                            letterSpacing: "2.5px",
                                            textTransform: "uppercase",
                                            py: "0.95rem",
                                            borderRadius: "10px",
                                            background: "linear-gradient(180deg, #1a1a1a 0%, #000000 100%)",
                                            color: "#ffffff",
                                            border: "1px solid #000000",
                                            boxShadow: "0 10px 30px rgba(0,0,0,0.22)",
                                            transition: "all 0.4s cubic-bezier(0.23,1,0.32,1)",
                                            "& .MuiButton-endIcon": { ml: 1.2, transition: "transform 0.4s ease" },
                                            "&:hover": {
                                                background: "linear-gradient(180deg, #000 0%, #000 100%)",
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 18px 44px rgba(0,0,0,0.30)",
                                                "& .MuiButton-endIcon": { transform: "translate(3px, -3px)" },
                                            },
                                        }}
                                    >
                                        {t("servicesPage.modal.book")}
                                    </Button>
                                </DialogContent>
                            </Box>
                        );
                    })()}
            </Dialog>
        </Box>
    );
};

export default ServicesPage;
